<template>
  <div class="main-wrapper">
    <div class="pannel-wrapper">
      <div class="annotations-wrapper">
        <div class="annotations-wrapper-header">
          <img
            src="@/assets/icons/newEyeOpenIcon.svg"
            alt="eye"
            class="eye"
            @click="hideAllAnnotations"
            v-if="annotationsVisible && !anonymousUser"
          />
          <img
            src="@/assets/icons/newEyeClosedIcon.svg"
            alt="eye"
            class="eye-closed"
            @click="showAllAnnotations"
            v-if="!annotationsVisible && !anonymousUser"
          />
          <div
            class="section-header-right-side"
            @click="toggleAnnotations"
            :class="{ inactiveGray: !annotationsVisible }"
          >
            <p class="cluster-title">{{ $t("sidebar.damages") }}</p>
            <img
              src="@/assets/icons/newArrowUp.svg"
              alt="triangle"
              class="arrow"
              v-show="showAnnotations"
            />
            <img
              src="@/assets/icons/newArrowDown.svg"
              alt="triangle"
              class="arrow"
              v-show="!showAnnotations"
            />
          </div>
        </div>
        <div v-if="showAnnotations" class="annotations-details-box">
          <div
            v-for="(ann, index) in annotations"
            :key="ann.id ?? index"
            class="annotation-inside-wrapper"
          >
            <div
              class="annotation-header"
              :class="{ active: ann.id == selectedAnnotationId }"
              @click="$emit('selectAnnotation', ann)"
            >
              <span
                class="severity-dot"
                :style="{ backgroundColor: severityColor(ann.severity_level) }"
              ></span>
              <div class="left-text">
                <p>{{ $t("sidebar.damageItem", { n: index + 1 }) }}</p>
              </div>
              <!-- The damage type is the only thing the row drops next to the
                   old layout, so it stays reachable on hover. -->
              <q-tooltip anchor="center right" self="center left">{{
                getDisplayLabel(ann)
              }}</q-tooltip>
              <span v-if="ann.automatically_evaluated" class="ai-badge">AI</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  annotations: {
    type: Array,
    default: () => [],
  },
  selectedAnnotationId: {
    type: [String, Number],
    default: null,
  },
  active: {
    type: Boolean,
    default: false,
  },
  anonymousUser: {
    type: Boolean,
    default: false,
  },
  hideAnnotationMarkers: {
    type: Function,
    default: null,
  },
  showAnnotationMarkers: {
    type: Function,
    default: null,
  },
  config: {
    type: Object,
    default: () => ({}),
  },
  setVisibilityInStore: {
    type: Function,
    default: null,
  },
  objectIsloaded: {
    type: [Boolean, Object],
    default: null,
  },
});

const emit = defineEmits(["selectAnnotation"]);

const showAnnotations = ref(true);
// The eye icon mirrors the persisted flag: this component only mounts once the
// annotations are loaded, which is after the project config, so the store
// already holds the saved answer. The markers themselves are restored by the
// viewer when they are created; only the icon has to be kept in step here.
const annotationsVisible = ref(!!props.config?.annotations_visible);

watch(
  () => props.config?.annotations_visible,
  (isVisible) => {
    annotationsVisible.value = !!isVisible;
  }
);

function hideAllAnnotations() {
  props.hideAnnotationMarkers?.();
  annotationsVisible.value = false;
  props.setVisibilityInStore?.("annotations_visible", false);
}

function showAllAnnotations() {
  props.showAnnotationMarkers?.();
  annotationsVisible.value = true;
  props.setVisibilityInStore?.("annotations_visible", true);
}

function showAnnotationsFlag() {
  if (!annotationsVisible.value) {
    showAllAnnotations();
  }
}

defineExpose({ showAnnotationsFlag });

function toggleAnnotations() {
  if (props.annotations.length) {
    showAnnotations.value = !showAnnotations.value;
  }
}

const DAMAGE_TYPE_KEY_MAP = {
  "Harmful plant growth": "harmful_plant_growth",
  "Defects at roof joints and edges": "defects_at_roof_joints_and_edges",
  "Damages in drainage system": "damages_in_drainage_system",
  "Defects around windows and doors": "defects_around_windows_and_doors",
  "Defects at roof penetrations": "defects_at_roof_penetrations",
  "Defective liquid waterproofing connections": "defective_liquid_waterproofing_connections",
  "Defects at waterproof joints": "defects_at_waterproof_joints",
  "Improper roof slope": "improper_roof_slope",
  "Damage of the material": "damage_of_the_material",
};

function getDisplayLabel(ann) {
  const dt = ann.damage_type;
  if (!dt || dt === "None") return "—";
  const key = DAMAGE_TYPE_KEY_MAP[dt];
  if (key) return t(`annotationPanel.damageTypes.${key}`);
  return dt;
}

const SEVERITY_COLORS = ["#8c8c8c", "#52c41a", "#a0d911", "#faad14", "#fa8c16", "#f5222d"];

function severityColor(level) {
  return SEVERITY_COLORS[level] ?? "#8c8c8c";
}
</script>

<style lang="scss" scoped>
.main-wrapper {
  .pannel-wrapper {
    max-height: 31rem;
    overflow-y: scroll;
    overflow-x: hidden;
    -ms-overflow-style: none;
    scrollbar-width: none;

    .annotations-wrapper {
      .annotations-wrapper-header {
        position: sticky;
        top: 0;
        z-index: 1;
        background: var(--color-dark-bg-secondary);
        max-height: 30px;
        display: flex;
        gap: 1rem;
        align-items: center;
        padding: 1rem 0;

        .section-header-right-side {
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          width: 100%;
          align-items: center;
        }

        .section-header-right-side.inactiveGray {
          p {
            color: gray;
          }
          img {
            filter: grayscale(1) brightness(0.5);
          }
        }

        p {
          font-family: "Roboto";
          font-size: 14px;
          font-style: normal;
          font-weight: bold;
          line-height: 19px;
          color: white;
          letter-spacing: 0.03em;
        }

        img {
          height: 12px;
          width: 18px;
          cursor: pointer;
        }

        .arrow {
          height: 20px;
          width: 20px;
        }
      }

      .annotations-details-box {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        margin-bottom: 12px;
        margin-top: 6px;

        .annotation-inside-wrapper {
          .annotation-header {
            background: var(--color-dark-bg-primary);
            border: 1px solid var(--color-dark-bg-tertiary);
            display: flex;
            gap: 8px;
            align-items: center;
            padding: 0.75rem 0.5rem;
            max-height: 30px;
            border-radius: 10px;
            justify-content: space-between;
            cursor: pointer;

            &.active {
              border-color: var(--color-gree300);
            }

            &:hover {
              border-color: var(--color-gree300);
              opacity: 0.85;
            }

            .left-text {
              flex: 1;
              width: 0;
              min-width: 0;
              overflow: hidden;

              p {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                cursor: pointer;
                color: white;
                font-family: "Roboto";
                font-size: 14px;
              }
            }

            .severity-dot {
              flex-shrink: 0;
              width: 8px;
              height: 8px;
              border-radius: 50%;
            }

            .ai-badge {
              flex-shrink: 0;
              padding: 2px 6px;
              border-radius: 4px;
              background: var(--color-dark-bg-tertiary, #4a4a4a);
              color: #d9d9d9;
              font-family: "Roboto";
              font-size: 11px;
              font-weight: 700;
              letter-spacing: 0.04em;
            }
          }
        }
      }
    }
  }
}

.eye:hover {
  filter: brightness(0) invert(1);
}
.eye-closed {
  filter: grayscale(1) brightness(1);
}
</style>
